export interface ToolCallState {
  id: string;
  tool: string;
  status: "running" | "done" | "error";
}

// ToolGroup aggregates calls of the same tool so the UI shows a single
// "Looking up places (12 / 15)" chip instead of 15 separate chips.
export interface ToolGroup {
  tool: string;
  total: number;
  done: number;
  errored: number;
  running: number;
}

const TOOL_LABELS: Record<string, string> = {
  update_itinerary: "Updating itinerary",
  get_place_info: "Looking up places",
  search_flights: "Searching flights",
  search_hotels: "Searching hotels",
};

const TOOL_ICONS: Record<string, string> = {
  update_itinerary: "📝",
  get_place_info: "📍",
  search_flights: "✈️",
  search_hotels: "🏨",
};

// groupToolCalls aggregates a flat list of ToolCallState into one group
// per distinct tool name, preserving the order each tool first appeared.
export function groupToolCalls(calls: ToolCallState[]): ToolGroup[] {
  const order: string[] = [];
  const map = new Map<string, ToolGroup>();
  for (const c of calls) {
    let g = map.get(c.tool);
    if (!g) {
      g = { tool: c.tool, total: 0, done: 0, errored: 0, running: 0 };
      map.set(c.tool, g);
      order.push(c.tool);
    }
    g.total++;
    if (c.status === "done") g.done++;
    else if (c.status === "error") g.errored++;
    else g.running++;
  }
  return order.map((t) => map.get(t)!);
}

interface ToolCallChipProps {
  group: ToolGroup;
}

export function ToolCallChip({ group }: ToolCallChipProps) {
  const label = TOOL_LABELS[group.tool] || group.tool;
  const icon = TOOL_ICONS[group.tool] || "🔧";
  const base =
    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors";

  // Running: any in-flight calls. Show count "X / total".
  if (group.running > 0) {
    return (
      <span className={`${base} bg-accent/15 border-accent/35 text-accent-light`}>
        <span>{icon}</span>
        <Spinner />
        <span>{label}</span>
        {group.total > 1 && (
          <span className="opacity-70">
            {group.done}/{group.total}
          </span>
        )}
      </span>
    );
  }

  // All done (no errors).
  if (group.errored === 0) {
    return (
      <span className={`${base} bg-electric/15 border-electric/35 text-electric`}>
        <span>{icon}</span>
        <span>✓</span>
        <span>{label}</span>
        {group.total > 1 && <span className="opacity-70">×{group.total}</span>}
      </span>
    );
  }

  // Some errored.
  return (
    <span className={`${base} bg-red-400/15 border-red-300/35 text-red-200`}>
      <span>{icon}</span>
      <span>⚠</span>
      <span>{label}</span>
      {group.total > 1 && (
        <span className="opacity-70">
          {group.errored}/{group.total} failed
        </span>
      )}
    </span>
  );
}

function Spinner() {
  return (
    <svg
      className="animate-spin w-3 h-3"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
